import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { formatDateCn, getRelatedNews } from "@/utils/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = newsList.find((news) => news.id === id);

  useDocumentMeta(
    item ? `${item.title} - 日韩影视资讯 | 日韩影视大全` : "资讯未找到 - 日韩影视大全",
    item ? item.excerpt : "该资讯可能已下线，可以返回资讯列表查看其他日韩影视动态。",
    item ? item.tags.join(",") : "日韩影视资讯",
  );

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-newspaper-line"
          title="没有找到这篇资讯"
          description="链接可能已经失效，或者该专题已调整。可以回到资讯列表继续浏览。"
        >
          <Link
            to="/news"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
          >
            返回资讯列表
          </Link>
        </EmptyState>
      </div>
    );
  }

  const related = getRelatedNews(newsList, item.id, item.relatedIds, 3);
  const moreStories = newsList
    .filter((news) => news.id !== item.id && !related.some((entry) => entry.id === news.id))
    .slice(0, 3);

  return (
    <div className="w-full pb-16">
      <article className="mx-auto w-full max-w-[1440px] px-4 pt-8 md:px-8">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <Link to="/news" className="cursor-pointer transition-colors hover:text-primary-700">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <span className="line-clamp-1 max-w-[200px] text-foreground-900 md:max-w-none">{item.category}</span>
        </nav>

        <header className="mt-5 max-w-3xl">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-foreground-500">
            <span className="rounded-sm bg-accent-100 px-2 py-0.5 font-medium text-accent-900">
              {item.category}
            </span>
            <time dateTime={item.date}>{formatDateCn(item.date)}</time>
            <span className="text-background-400">·</span>
            <span>{item.readTime}</span>
            <span className="text-background-400">·</span>
            <span>{item.author}</span>
          </div>
          <h1 className="mt-3 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-[36px] md:leading-tight">
            {item.title}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{item.subtitle}</p>
        </header>

        <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={`${item.title} - 日韩影视资讯`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="max-w-3xl">
            <div className="flex flex-col gap-4">
              {item.body.map((paragraph, index) => (
                <p
                  key={paragraph.slice(0, 18) + index}
                  className="text-[15px] leading-[1.95] text-foreground-700"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-1.5">
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200/80 pt-5">
              <p className="text-xs text-foreground-500">本文为站点演示内容，不构成任何真实报道。</p>
              <div className="flex items-center gap-3">
                <Link
                  to="/news"
                  className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-foreground-600 transition-colors hover:text-primary-700"
                >
                  <i className="ri-arrow-left-line text-sm" />
                  返回资讯列表
                </Link>
                <Link
                  to="/"
                  className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-foreground-600 transition-colors hover:text-primary-700"
                >
                  回首页
                </Link>
              </div>
            </div>
          </div>

          <aside className="flex flex-col gap-4">
            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-foreground-950">相关文章</h2>
                <span className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-400">
                  Related
                </span>
              </div>
              <div className="mt-3 flex flex-col gap-3">
                {related.map((news) => (
                  <NewsCard key={news.id} item={news} variant="compact" />
                ))}
              </div>
            </section>

            <section className="rounded-lg bg-primary-950 p-5">
              <p className="font-label text-[11px] uppercase tracking-[0.2em] text-accent-300">Quick Links</p>
              <h2 className="mt-2 font-heading text-base font-semibold text-background-50">继续浏览</h2>
              <div className="mt-3 flex flex-col gap-2">
                {[
                  { to: "/category/movie", label: "日韩电影分类" },
                  { to: "/category/tv", label: "日韩电视剧分类" },
                  { to: "/category/variety", label: "日韩综艺分类" },
                  { to: "/search", label: "搜索影视作品" },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex cursor-pointer items-center justify-between rounded-md border border-background-50/15 px-3 py-2.5 text-xs text-background-200 transition-colors hover:border-accent-400/60 hover:text-background-50"
                  >
                    {link.label}
                    <i className="ri-arrow-right-s-line text-base" />
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>

        <section className="mt-10 border-t border-background-200/80 pt-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">
                <span className="h-px w-6 bg-accent-500" />
                More Stories
              </p>
              <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950">更多影视资讯</h2>
            </div>
            <Link
              to="/news"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700"
            >
              全部资讯
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {moreStories.map((news) => (
              <NewsCard key={`grid-${news.id}`} item={news} />
            ))}
          </div>
        </section>
      </article>
    </div>
  );
}